import type { ReactNode } from 'react'
import cx from 'classnames'
import styles from './JobsListEmpty.module.css'
import {
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateIllustration,
  EmptyStateTitle,
} from '@/components/EmptyState'
import { Button } from '@/components/Button'
import { byDesign } from '@/infra/design'
import imgSearchNoresultsUrl from '@/assets/misc/img_search-noresults.svg'
import SearchNoResultsIllustration from '@/components/SpotIllustration/SearchNoResultsIllustration'
import type { FittedIllustration } from '@/components/SpotIllustration/fitted-illustration'

export interface JobsListEmptyProps {
  /**
   * Empty state title.
   */
  title?: string
  /**
   * Empty state subtitle.
   */
  subtitle?: ReactNode
  /**
   * Illustration image URL.
   */
  imageSrc?: string
  /**
   * Illustration shown instead of `imageSrc` when set.
   */
  illustration?: FittedIllustration
  /**
   * Callback for clearing the search filter.
   */
  onClearSearchFilter?: () => unknown
  /**
   * Callback for setting the job scope.
   */
  onSetJobScope?: () => unknown
  /**
   * Children.
   */
  children?: ReactNode
  /**
   * Whether the workspace wants a corporate look, which hides illustrations.
   */
  corporateMode: boolean
}

export function JobsListEmpty({
  subtitle,
  onClearSearchFilter,
  onSetJobScope,
  title = 'We looked everywhere...',
  imageSrc,
  illustration,
  children,
  corporateMode,
}: JobsListEmptyProps) {
  subtitle = subtitle || (
    <>
      But we couldn't find what you were looking for.
      {onClearSearchFilter && (
        <>
          <br />
          Maybe try{' '}
          <Button
            label="clearing your search filter"
            size="baseline"
            onPress={onClearSearchFilter}
            variant="text"
          />{' '}
          {onSetJobScope && (
            <>
              or try{' '}
              <Button
                label="looking in all jobs"
                size="baseline"
                onPress={onSetJobScope}
                variant="text"
              />
            </>
          )}
          ?
        </>
      )}
    </>
  )
  return (
    <div className={cx(styles.root)}>
      <EmptyState size="sm">
        {!corporateMode && (
          <EmptyStateIllustration
            src={imageSrc ?? imgSearchNoresultsUrl}
            illustration={
              illustration ??
              // A caller's own image replaces the default in either design.
              (imageSrc
                ? undefined
                : byDesign(undefined, SearchNoResultsIllustration))
            }
          />
        )}
        <EmptyStateTitle>{title}</EmptyStateTitle>
        <EmptyStateDescription>{subtitle}</EmptyStateDescription>
        {children && <EmptyStateActions>{children}</EmptyStateActions>}
      </EmptyState>
    </div>
  )
}
